import { useCallback, useSyncExternalStore } from "react";

/** Chrome/Android fires `beforeinstallprompt`; iOS Safari never does (no native install API —
 * documented in `docs/runbooks/pwa-device-testing.md`), so iOS users get a hint with the
 * share-sheet steps instead of a button. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

/* The browser fires `beforeinstallprompt` once, early — often before any React component has
 * mounted — so it is captured at module level (`captureInstallPrompt()` is called from `main.tsx`)
 * and replayed to whichever component asks later. */
let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let captured = false;
let version = 0;
const listeners = new Set<() => void>();

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** Starts listening for the install events (idempotent; safe on the server / in tests). */
export function captureInstallPrompt(): void {
  if (captured || typeof window === "undefined") return;
  captured = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // keep the event so the prompt opens only from our own button
    deferred = event as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    emit();
  });
}

captureInstallPrompt();

/** Test helper: forget any captured event. */
export function resetInstallPromptForTests(): void {
  deferred = null;
  installed = false;
  emit();
}

export function detectIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports a desktop Mac UA; touch support tells it apart from a real Mac.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

export function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (installed) return true;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayMode =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  return iosStandalone || displayMode;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export interface InstallPromptState {
  /** A native install prompt is available and the app is not already installed. */
  readonly canInstall: boolean;
  /** Opens the native prompt; resolves with the user's choice. */
  readonly install: () => Promise<InstallOutcome>;
  readonly isIos: boolean;
  /** Already running as an installed app (display-mode standalone / iOS home-screen launch). */
  readonly isStandalone: boolean;
}

export function useInstallPrompt(): InstallPromptState {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => 0,
  );
  const isStandalone = detectStandalone();
  const install = useCallback(async (): Promise<InstallOutcome> => {
    const event = deferred;
    if (!event) return "unavailable";
    deferred = null; // a captured event can be prompted only once
    emit();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  }, []);
  return {
    canInstall: deferred !== null && !isStandalone,
    install,
    isIos: detectIos(),
    isStandalone,
  };
}
